/**
 * The bounded wait for the login session on a hard page load (request-auth phase 4, prototype 2026-10-19).
 *
 * ── Why ───────────────────────────────────────────────────────────────────────────────────────────
 * The token interceptor never waits for Firebase (src/plugins/authTokenInterceptor.js: "ZERO WAIT"), so on a hard page load the
 * first-wave calls go out before Firebase has restored the session and carry no token. MEASURED 2026-10-19 on dev (10 hard loads
 * of /app/campaigns, real Chrome): the first-wave staff GETs fire ~700-950 ms after navigation tokenless, and the first request
 * carrying a token follows 131-190 ms later (median ~160 ms). Today that is harmless (nothing enforces). The day a backend route
 * enforces the login check, every hard load would be refused its first wave. This module closes that gap.
 *
 * ── The rule ──────────────────────────────────────────────────────────────────────────────────────
 * A request the token policy WOULD tag (wouldAttachIfSignedIn) that is fired before Firebase has said who is signed in is HELD
 * until the session is known, for at most SESSION_WAIT_CAP_MS counted from app start (not per request). Then it goes out, with the
 * token if there is one. Everything else — requests the policy never tags, requests after the session is known (including
 * "nobody is signed in"), requests after the cap — is untouched and sent synchronously, exactly as today.
 *
 * ── Why it still honours "page load not slower" ─────────────────────────────────────────────────
 * It is only the first calls of a hard load, only the ones that would have been refused anyway, only until Firebase answers
 * (~0.2 s measured), and never longer than the cap even if Firebase never answers. In-app navigation never waits.
 *
 * Plain functions with injectable clock/timers so cypress/node-tests/auth-session-gate.test.js runs the exact shipped file.
 * The wiring (Firebase listener + axios registration) stays in src/plugins/authTokenInterceptor.js.
 */

import { applyAuthHeader, wouldAttachIfSignedIn } from '@/utils/authHeaderPolicy';

// Longest a request is held for the session. Counted from the gate's creation (app start), so a hung Firebase delays only the
// first calls, once. The measured need is ~0.2 s warm; the rest is headroom for a token refresh over a slow network.
export const SESSION_WAIT_CAP_MS = 2000;

/**
 * settle()   — Firebase has answered (a user with a token, or nobody, or a failure): nothing waits any more.
 * isSettled() — has that happened.
 * wait()     — resolves 'settled' when it does, or 'cap' when the cap from creation is reached. Never rejects.
 */
export function createSessionGate({ capMs = SESSION_WAIT_CAP_MS, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  const startedAt = now();
  let settled = false;
  let resolveSettled;
  const settledPromise = new Promise((resolve) => { resolveSettled = resolve; });
  return {
    settle() {
      if (settled) return;
      settled = true;
      resolveSettled();
    },
    isSettled() { return settled; },
    wait() {
      if (settled) return Promise.resolve('settled');
      const left = startedAt + capMs - now();
      if (left <= 0) return Promise.resolve('cap');
      return new Promise((resolve) => {
        const timer = setTimer(() => resolve('cap'), left);
        settledPromise.then(() => { clearTimer(timer); resolve('settled'); });
      });
    },
  };
}

/**
 * The axios request-interceptor body: applyAuthHeader, preceded by the bounded wait when (and only when) the request would carry
 * a token but the session is not known yet.
 *   gate    — a createSessionGate()
 *   apiBase — the env API base
 *   getCtx  — () => { apiBase, token, expiresAt, now, signedIn } read fresh each call (the token may arrive while a request waits)
 * Returns the config (synchronously) or a Promise of it. Never throws, never rejects: on any surprise the request goes out as today.
 */
export function createAuthRequestHandler({ gate, apiBase, getCtx }) {
  return function handleRequest(config) {
    try {
      if (!gate.isSettled() && wouldAttachIfSignedIn(config, apiBase)) {
        return gate.wait().then(() => safeApply(config, getCtx), () => config);
      }
      return applyAuthHeader(config, getCtx());
    } catch (_e) {
      return config;
    }
  };
}

function safeApply(config, getCtx) {
  try {
    return applyAuthHeader(config, getCtx());
  } catch (_e) {
    return config;
  }
}
